import store from '@/store'
import { getEmbedStatus } from '@/hooks/use-embed-mode'
import { ensureEmbeddedBoardsLoaded } from '@/services/dayforce-board-resolution.service'
import { logger } from '@/utils/logger'

/**
 * BoardNavigationService
 *
 * Comprehensive service to handle all board navigation concerns including:
 * - Validating board IDs during navigation
 * - Ensuring proper redirects for invalid board access attempts
 * - Persisting user's default board selections
 * - Protecting against race conditions during board data loading
 * - Managing board transition analytics
 */
export class BoardNavigationService {
  constructor() {
    this.store = store
    this.localStoragePrefix = 'an-'
  }

  /**
   * Validates a route's boardId parameter and returns appropriate redirect if needed
   * @param {Route} to - Target route object from Vue Router
   * @returns {Route|null} Modified route if redirect needed, null if route is valid
   */
  async validateRouteNavigation(to) {
    // Skip validation for non-authenticated routes
    if (!to.meta.requiresAuth) return null

    // Skip all boardId validation for routes that don't require it
    if (!this.routeRequiresBoardId(to)) {
      return null
    }

    // Wait for boards and invitations to load if needed
    await Promise.all([this.ensureBoardsLoaded(), this.ensureInvitationsLoaded()])

    // For routes that should have a boardId but don't
    if (!to.params.boardId) {
      return this.createRedirectToDefaultBoard(to)
    }

    // For routes with boardId that user doesn't have access to
    if (!this.isValidBoardId(to.params.boardId)) {
      logger.warn(`Invalid boardId: ${to.params.boardId}, redirecting to default board`)
      return this.createRedirectToDefaultBoard(to)
    }

    // For valid navigation, persist this board as default
    this.persistDefaultBoard(to.params.boardId)

    // Route is valid, no redirect needed
    return null
  }

  /**
   * Checks if a specific boardId is valid for the current user
   * @param {string} boardId - The board ID to validate
   * @returns {boolean} Whether the board is valid for this user
   */
  isValidBoardId(boardId) {
    if (!boardId) return false

    const boards = this.store.getters.mainBoards || []
    return boards.some((board) => board.boardId === boardId)
  }

  /**
   * Determines if a route requires a boardId param
   * @param {Route} route - Vue Router route object
   * @returns {boolean} Whether the route should have a boardId
   */
  routeRequiresBoardId(route) {
    // All authenticated routes except these specific exceptions need a boardId
    const pathsWithoutBoardId = [
      '/oops',
      '/setup-user',
      '/verify',
      '/update-authentication',
      '/no-boards',
      '/embed-no-boards',
      '/invitations',
      '/super-admin'
    ]

    // Routes that explicitly don't need boardId validation
    const routesWithoutBoardId = [
      'Home',
      'No Access',
      'No Boards',
      'EmbedNoBoards',
      'DayforceOnboarding', // Board may not be in mainBoards during initial setup
      'PendingInvitations',
      'Super Admin',
      'Notifications'
    ]

    // Special case for routes that explicitly don't need boardId
    if (routesWithoutBoardId.includes(route.name)) {
      return false
    }

    // Special case for Landing route without a boardId - it needs a boardId
    if (route.name === 'Landing' && !route.params.boardId) {
      return true
    }

    return (
      route.meta.requiresAuth && !pathsWithoutBoardId.some((path) => route.path.startsWith(path))
    )
  }

  /**
   * Creates a redirect to the appropriate default board
   * @param {Route} originalRoute - The original attempted route
   * @returns {Route} A new route object with valid boardId
   */
  createRedirectToDefaultBoard(originalRoute) {
    const defaultBoardId = this.getDefaultBoardId()

    if (!defaultBoardId) {
      // No valid boards found - check if user is in embed mode
      const { isEmbedded, isTrustedEmbed } = getEmbedStatus()

      if (isEmbedded && isTrustedEmbed) {
        logger.warn(
          'No valid boards available for embedded user, redirecting to Embed No Boards view'
        )
        return { name: 'EmbedNoBoards' }
      }

      // Regular user with no boards - redirect to the "No Boards" view
      logger.warn('No valid boards available for user, redirecting to No Boards view')
      return { name: 'No Boards' }
    }

    // Preserve the route name and query params, just update the boardId
    return {
      name: originalRoute.name,
      params: { ...originalRoute.params, boardId: defaultBoardId },
      query: { ...originalRoute.query }
    }
  }

  /**
   * Gets the default board ID based on user preferences and available boards
   * Prioritizes user's saved board preference over invitations
   * @returns {string|null} The default board ID or null if none available
   */
  getDefaultBoardId() {
    const userId = this.store.getters.uid
    const boards = this.store.getters.mainBoards || []

    if (!userId || !boards.length) return null

    // Try to get saved default from localStorage first (respects user preference)
    const localStorageKey = `${this.localStoragePrefix}${userId}`
    const savedDefault = localStorage.getItem(localStorageKey)

    // If saved default exists and is valid, use it
    if (savedDefault && this.isValidBoardId(savedDefault)) {
      return savedDefault
    }

    // Otherwise fall back to first available board
    return boards[0]?.boardId || null
  }

  /**
   * Gets the default board ID for super admin
   * @returns {string|null} The default board ID or null if none available
   */
  getDefaultBoardIdForSuperAdmin() {
    const userId = this.store.getters.uid
    const boards = this.store.getters.mainBoards || []

    if (!userId || !boards.length) return null

    // Try to get saved default from localStorage
    const localStorageKey = `${this.localStoragePrefix}${userId}`
    const savedDefault = localStorage.getItem(localStorageKey)

    // If saved default exists and is valid, use it
    if (savedDefault && this.isValidBoardId(savedDefault)) {
      return savedDefault
    }

    return null
  }

  /**
   * Persists a board ID as the user's default
   * @param {string} boardId - The board ID to set as default
   */
  persistDefaultBoard(boardId) {
    const userId = this.store.getters.uid
    if (!userId || !boardId) return

    // Only update if valid board
    if (this.isValidBoardId(boardId)) {
      const localStorageKey = `${this.localStoragePrefix}${userId}`
      localStorage.setItem(localStorageKey, boardId)

      // Log for analytics
      if (window.mixpanel) {
        window.mixpanel.track('set_default_board', { boardId })
      }
    }
  }

  async ensureBoardsLoaded() {
    const user = this.store.getters.user
    if (!user?.uid) return

    const { isEmbedded, isTrustedEmbed } = getEmbedStatus()
    if (isEmbedded && isTrustedEmbed) {
      await ensureEmbeddedBoardsLoaded({ store: this.store })
      return
    }

    await this.store.dispatch('fetchAllBoardsIfMissing', user.uid)
  }

  /**
   * Ensures invitations are loaded for the current user
   * @returns {Promise<void>}
   */
  async ensureInvitationsLoaded() {
    const user = this.store.getters.user
    if (!user || !user.email) return

    // Only fetch if invitations haven't been loaded yet
    if (this.store.state.user.invitations === null) {
      await this.store.dispatch('fetchPendingInvitations', { email: user.email })
    }
  }

  /**
   * Registers board change events for analytics
   * @param {string} fromBoardId - Previous board ID
   * @param {string} toBoardId - New board ID
   */
  trackBoardTransition(fromBoardId, toBoardId) {
    if (fromBoardId === toBoardId) return

    // Skip tracking if either ID is invalid or missing
    if (!fromBoardId || !toBoardId) return

    // Capture transition for analytics
    if (window.mixpanel) {
      window.mixpanel.track('board_transition', {
        from_board: fromBoardId,
        to_board: toBoardId
      })
    }

    // Could extend with application telemetry, performance monitoring, etc.
  }
}

// Export singleton instance
export const boardNavigationService = new BoardNavigationService()
